import React, { useState, useEffect, useRef } from 'react';
import { Play, X, Video as VideoIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { getVerifiedTestimonials } from '../services/api';
import axios from 'axios';

const Testimonials = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [testimonialVideos, setTestimonialVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);

  // Helper function to extract YouTube video ID and get thumbnail
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    
    let videoId = null;
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  // Helper function to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    let videoId = null;
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtube.com/embed/')) {
      // Already in embed format
      return url;
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    return url;
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 150;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const fetchTestimonialVideos = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${API}/api/videos`);
        
        // Filter only testimonial/influencer videos that are active
        const testimonials = response.data
          .filter(video => (video.type === 'testimonial' || video.type === 'influencer') && video.isActive)
          .map((video, index) => ({
            id: video._id || index,
            title: video.title,
            videoUrl: video.url,
            thumbnail: video.thumbnail || getYouTubeThumbnail(video.url)
          }));
        
        // If no videos, show placeholder cards
        if (testimonials.length === 0) {
          setTestimonialVideos([
            { id: 'placeholder-1', title: 'Customer Story 1', videoUrl: '', thumbnail: null },
            { id: 'placeholder-2', title: 'Customer Story 2', videoUrl: '', thumbnail: null },
            { id: 'placeholder-3', title: 'Customer Story 3', videoUrl: '', thumbnail: null },
            { id: 'placeholder-4', title: 'Customer Story 4', videoUrl: '', thumbnail: null }
          ]);
        } else {
          setTestimonialVideos(testimonials);
        }
      } catch (error) {
        console.error('Failed to fetch testimonial videos:', error);
        // Show placeholder cards on error
        setTestimonialVideos([
          { id: 'placeholder-1', title: 'Customer Story 1', videoUrl: '', thumbnail: null },
          { id: 'placeholder-2', title: 'Customer Story 2', videoUrl: '', thumbnail: null },
          { id: 'placeholder-3', title: 'Customer Story 3', videoUrl: '', thumbnail: null },
          { id: 'placeholder-4', title: 'Customer Story 4', videoUrl: '', thumbnail: null }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonialVideos();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white text-xl">Loading testimonials...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-6xl font-black text-white leading-tight">
            Real Jumps, <span className="text-yellow-400">Real Thrill</span>
          </h2>
          <h2 className="text-3xl md:text-6xl font-black text-white">
            Real Stories
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-200 mx-auto mt-6"></div>
        </div>

        {/* Desktop View - Grid Layout */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-6">
          {testimonialVideos.map((video, index) => (
            <div
              key={video.id}
              className={`group relative h-80 rounded-2xl overflow-hidden transform hover:scale-105 transition-all duration-500 bg-gradient-to-br from-gray-900 via-gray-800 to-black border-2 border-gray-700 hover:border-yellow-400 ${video.videoUrl ? 'cursor-pointer' : 'cursor-default'}`}
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
              }}
              onClick={() => video.videoUrl && setSelectedVideo(video)}
            >
              {/* Video Thumbnail or Preview */}
              {video.thumbnail ? (
                <img 
                  src={video.thumbnail} 
                  alt={video.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : video.videoUrl && !video.videoUrl.includes('youtube') && !video.videoUrl.includes('youtu.be') ? (
                <video 
                  className="absolute inset-0 w-full h-full object-cover"
                  preload="metadata"
                  muted
                  playsInline
                >
                  <source src={video.videoUrl + '#t=0.1'} type="video/mp4" />
                </video>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <VideoIcon className="text-gray-700 group-hover:text-gray-600 transition-colors" size={120} strokeWidth={1} />
                </div>
              )}

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-all duration-300"></div>

              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-yellow-400 group-hover:bg-yellow-300 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shadow-lg shadow-yellow-400/50">
                  <Play className="text-black ml-1" size={32} fill="black" />
                </div>
              </div>

              {/* Title */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent">
                <h3 className="text-2xl font-black text-white text-center">{video.title}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile/Tablet View - Horizontal Scrollable Carousel */}
        <div className="lg:hidden relative">
          {/* Scroll Buttons */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-yellow-400 hover:bg-yellow-300 text-black p-2 rounded-full shadow-lg"
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-yellow-400 hover:bg-yellow-300 text-black p-2 rounded-full shadow-lg"
            aria-label="Scroll right"
          >
            <ChevronRight size={18} />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth px-10 py-4"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {testimonialVideos.map((video, index) => (
              <div
                key={video.id}
                className={`flex-shrink-0 w-32 h-[200px] rounded-2xl overflow-hidden shadow-2xl ${video.videoUrl ? 'cursor-pointer' : 'cursor-default'}`}
                style={{
                  scrollSnapAlign: 'center',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
                onClick={() => video.videoUrl && setSelectedVideo(video)}
              >
                {/* Background Image/Video */}
                <div className="relative w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-black">
                  {video.thumbnail ? (
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : video.videoUrl && !video.videoUrl.includes('youtube') && !video.videoUrl.includes('youtu.be') ? (
                    <video 
                      className="w-full h-full object-cover"
                      preload="metadata"
                      muted
                      playsInline
                    >
                      <source src={video.videoUrl + '#t=0.1'} type="video/mp4" />
                    </video>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <VideoIcon className="text-gray-700" size={48} strokeWidth={1} />
                    </div>
                  )}

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>

                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg shadow-yellow-400/50">
                      <Play className="text-black ml-0.5" size={20} fill="black" />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="absolute inset-0 flex flex-col justify-end p-3">
                    <h3 className="text-sm font-black text-white text-center">
                      {video.title}
                    </h3>
                  </div>

                  {/* Border Effect */}
                  <div className="absolute inset-0 border-2 border-yellow-400/50 rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-5xl">
            {/* Close Button */}
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors"
              aria-label="Close video"
            >
              <X size={32} />
            </button>

            {/* Video */}
            <div className="aspect-w-16 aspect-h-9 rounded-2xl overflow-hidden shadow-2xl">
              {selectedVideo.videoUrl.includes('youtube.com') || selectedVideo.videoUrl.includes('youtu.be') ? (
                <iframe
                  className="w-full h-96 md:h-[500px] rounded-2xl"
                  src={getYouTubeEmbedUrl(selectedVideo.videoUrl)}
                  title={selectedVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video
                  className="w-full h-96 md:h-[500px] rounded-2xl bg-black"
                  controls
                  autoPlay
                  src={selectedVideo.videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for hiding scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default Testimonials;